import classNames from 'classnames';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useCallback } from 'react';
import UDText from 'ud-ui/components/text';

import { PagerContainer, PagerLink } from './styles';

/** Сколько номеров помещается в окне вокруг текущей страницы. */
const DEFAULT_PAGES_SHOW = 10;

export interface UDPagedListPagerProps {
  /** Наибольший известный номер страницы. При `<= 1` пагинатор не рисуется. */
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  /**
   * Адрес страницы списка с другим номером. Без него ссылки остаются ссылками
   * (`href="#"`), но открыть страницу в новой вкладке уже нельзя.
   */
  pageHref?: (page: number) => string;
  pagesShow?: number;
  className?: string;
}

/** Окно номеров вокруг текущей страницы, прижатое к границам диапазона. */
function getPagesWindow(currentPage: number, totalPages: number, pagesShow: number): number[] {
  const size = Math.min(pagesShow, totalPages);
  const half = Math.floor((size - 1) / 2);

  let start = Math.max(1, currentPage - half);
  const end = Math.min(totalPages, start + size - 1);
  start = Math.max(1, end - size + 1);

  const pages: number[] = [];
  for (let page = start; page <= end; page++) {
    pages.push(page);
  }
  return pages;
}

/**
 * Пагинатор страницы-категории.
 *
 * От `UDPagination` отличается двумя вещами. Во-первых, номера — настоящие
 * `<a href>`: у старого компонента `getHref` объявлен, но нигде не используется,
 * поэтому средний клик и «открыть в новой вкладке» не работали. Обычный клик
 * перехватывается `preventDefault`, чтобы не было полной перезагрузки SPA.
 *
 * Во-вторых, при одной странице (или ни одной) пагинатор не рисуется вовсе:
 * `list-page-wrapper` рисовал его всегда, включая случай нулевого списка.
 */
const UDPagedListPager = ({
  totalPages,
  currentPage,
  onPageChange,
  pageHref,
  pagesShow = DEFAULT_PAGES_SHOW,
  className,
}: UDPagedListPagerProps) => {
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      // Модифицирующий клик (новая вкладка, новое окно) отдаём браузеру.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      event.preventDefault();
      onPageChange(Number(event.currentTarget.dataset.page));
    },
    [onPageChange],
  );

  if (totalPages <= 1) return null;

  const pages = getPagesWindow(currentPage, totalPages, pagesShow);
  const hasPrev = currentPage > 1;
  const hasNext = currentPage < totalPages;

  const renderLink = (page: number, key: string, content: React.ReactNode, extraClass?: Record<string, boolean>) => (
    <PagerLink
      key={key}
      href={pageHref ? pageHref(page) : '#'}
      data-page={page}
      onClick={handleClick}
      className={classNames(extraClass)}
    >
      {content}
    </PagerLink>
  );

  return (
    <PagerContainer className={className} aria-label="Страницы списка">
      {renderLink(currentPage - 1, 'prev', <ChevronLeft size={22} aria-label="Предыдущая страница" />, {
        disallow: !hasPrev,
      })}

      {pages.map((page) =>
        renderLink(page, `page-${page}`, <UDText type="footnote">{page}</UDText>, { active: page === currentPage }),
      )}

      {renderLink(currentPage + 1, 'next', <ChevronRight size={22} aria-label="Следующая страница" />, {
        disallow: !hasNext,
      })}
    </PagerContainer>
  );
};

export default UDPagedListPager;
